import { c as _c } from "react/compiler-runtime";
import { useId, type ChangeEvent } from "react";
interface TimePickerProps {
  readonly hours: number;
  readonly minutes: number;
  readonly onTimeChange: (hours: number, minutes: number) => void;
  readonly minTime?: {
    hours: number;
    minutes: number;
  } | null | undefined;
  readonly disabled?: boolean;
}
interface TimeOption {
  label: string;
  hours: number;
  minutes: number;
}
const formatTime = (timeHours: number, timeMinutes: number) => {
  const period = timeHours >= 12 ? "PM" : "AM";
  const hour12 = timeHours % 12 === 0 ? 12 : timeHours % 12;
  const minuteLabel = timeMinutes.toString().padStart(2, "0");
  return `${hour12}:${minuteLabel} ${period}`;
};
export default function TimePicker(t0) {
  const $ = _c(85);
  const {
    hours,
    minutes,
    onTimeChange,
    minTime: t1,
    disabled: t2
  } = t0;
  const minTime = t1 === undefined ? null : t1;
  const disabled = t2 === undefined ? false : t2;
  const baseId = useId();
  const hoursInputId = `${baseId}-hours`;
  const minutesInputId = `${baseId}-minutes`;
  const minTimeDescriptionId = minTime ? `${baseId}-min-time` : undefined;
  let t3;
  if ($[0] !== minTime) {
    t3 = minTime === null ? undefined : `Earliest selectable time is ${formatTime(minTime.hours, minTime.minutes)}.`;
    $[0] = minTime;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const minTimeDescription = t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== disabled || $[3] !== hours || $[4] !== hoursInputId || $[5] !== minTime || $[6] !== minTimeDescription || $[7] !== minTimeDescriptionId || $[8] !== minutes || $[9] !== minutesInputId || $[10] !== onTimeChange) {
    const timeOptions = [{
      label: "12 AM",
      hours: 0,
      minutes: 0
    }, {
      label: "6 AM",
      hours: 6,
      minutes: 0
    }, {
      label: "9 AM",
      hours: 9,
      minutes: 0
    }, {
      label: "12 PM",
      hours: 12,
      minutes: 0
    }, {
      label: "3 PM",
      hours: 15,
      minutes: 0
    }, {
      label: "6 PM",
      hours: 18,
      minutes: 0
    }, {
      label: "9 PM",
      hours: 21,
      minutes: 0
    }, {
      label: "11:59 PM",
      hours: 23,
      minutes: 59
    }];
    let displayHours;
    let isPm;
    let isTimeDisabled;
    let toggleAmPm;
    if ($[17] !== disabled || $[18] !== hours || $[19] !== minTime || $[20] !== minutes || $[21] !== onTimeChange) {
      toggleAmPm = () => {
        if (disabled) {
          return;
        }
        const newHours = hours >= 12 ? hours - 12 : hours + 12;
        if (isTimeDisabled(newHours, minutes)) {
          return;
        }
        onTimeChange(newHours, minutes);
      };
      isPm = hours >= 12;
      displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      isTimeDisabled = (h, m) => {
        if (!minTime) {
          return false;
        }
        if (h < minTime.hours) {
          return true;
        }
        if (h === minTime.hours && m < minTime.minutes) {
          return true;
        }
        return false;
      };
      $[17] = disabled;
      $[18] = hours;
      $[19] = minTime;
      $[20] = minutes;
      $[21] = onTimeChange;
      $[22] = displayHours;
      $[23] = isPm;
      $[24] = isTimeDisabled;
      $[25] = toggleAmPm;
    } else {
      displayHours = $[22];
      isPm = $[23];
      isTimeDisabled = $[24];
      toggleAmPm = $[25];
    }
    let t10;
    if ($[26] !== disabled || $[27] !== isPm || $[28] !== isTimeDisabled || $[29] !== minutes || $[30] !== onTimeChange) {
      t10 = e => {
        if (disabled) {
          return;
        }
        const val = parseInt(e.target.value, 10);
        if (isNaN(val)) {
          return;
        }
        const newHours_0 = isPm ? val === 12 ? 12 : val + 12 : val === 12 ? 0 : val;
        if (!isTimeDisabled(newHours_0, minutes)) {
          onTimeChange(newHours_0, minutes);
        }
      };
      $[26] = disabled;
      $[27] = isPm;
      $[28] = isTimeDisabled;
      $[29] = minutes;
      $[30] = onTimeChange;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    const onHoursChange = t10;
    let t11;
    if ($[32] !== disabled || $[33] !== hours || $[34] !== isTimeDisabled || $[35] !== onTimeChange) {
      t11 = e_0 => {
        if (disabled) {
          return;
        }
        const val_0 = parseInt(e_0.target.value, 10);
        if (isNaN(val_0)) {
          return;
        }
        if (!isTimeDisabled(hours, val_0)) {
          onTimeChange(hours, val_0);
        }
      };
      $[32] = disabled;
      $[33] = hours;
      $[34] = isTimeDisabled;
      $[35] = onTimeChange;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    const onMinutesChange = t11;
    t9 = `tw-relative tw-rounded-lg tw-bg-iron-800/60 tw-py-4 tw-shadow-md tw-ring-1 tw-ring-iron-700/50 ${disabled ? "tw-opacity-60" : ""}`;
    t6 = "tw-px-5";
    let t12;
    if ($[37] !== hoursInputId) {
      t12 = <label className="tw-sr-only" htmlFor={hoursInputId}>Hours</label>;
      $[37] = hoursInputId;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    const t13 = `tw-w-full tw-rounded-lg tw-border-0 tw-bg-[#2A2A33] tw-p-2 tw-ring-1 tw-ring-iron-700/30 tw-transition-all tw-duration-300 [appearance:textfield] [&::-webkit-inner-spin-button]:tw-appearance-none [&::-webkit-outer-spin-button]:tw-appearance-none ${disabled ? "tw-cursor-not-allowed tw-text-iron-500" : "tw-text-white focus:tw-outline-none focus:tw-ring-primary-400"}`;
    let t14;
    if ($[39] !== disabled || $[40] !== displayHours || $[41] !== hoursInputId || $[42] !== minTimeDescriptionId || $[43] !== onHoursChange || $[44] !== t13) {
      t14 = <input type="number" min="1" max="12" value={displayHours} onChange={onHoursChange} disabled={disabled} id={hoursInputId} aria-describedby={minTimeDescriptionId} className={t13} placeholder="HH" />;
      $[39] = disabled;
      $[40] = displayHours;
      $[41] = hoursInputId;
      $[42] = minTimeDescriptionId;
      $[43] = onHoursChange;
      $[44] = t13;
      $[45] = t14;
    } else {
      t14 = $[45];
    }
    let t15;
    if ($[46] !== t12 || $[47] !== t14) {
      t15 = <div className="tw-relative tw-w-20">{t12}{t14}</div>;
      $[46] = t12;
      $[47] = t14;
      $[48] = t15;
    } else {
      t15 = $[48];
    }
    let t16;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <span className="tw-font-bold tw-text-iron-50">:</span>;
      $[49] = t16;
    } else {
      t16 = $[49];
    }
    let t17;
    if ($[50] !== minutesInputId) {
      t17 = <label className="tw-sr-only" htmlFor={minutesInputId}>Minutes</label>;
      $[50] = minutesInputId;
      $[51] = t17;
    } else {
      t17 = $[51];
    }
    const t18 = `tw-w-full tw-rounded-lg tw-border-0 tw-bg-[#2A2A33] tw-p-2 tw-ring-1 tw-ring-iron-700/30 tw-transition-all tw-duration-300 [appearance:textfield] [&::-webkit-inner-spin-button]:tw-appearance-none [&::-webkit-outer-spin-button]:tw-appearance-none ${disabled ? "tw-cursor-not-allowed tw-text-iron-500" : "tw-text-white focus:tw-outline-none focus:tw-ring-primary-400"}`;
    let t19;
    if ($[52] !== disabled || $[53] !== minTimeDescriptionId || $[54] !== minutes || $[55] !== minutesInputId || $[56] !== onMinutesChange || $[57] !== t18) {
      t19 = <input type="number" min="0" max="59" value={minutes} onChange={onMinutesChange} disabled={disabled} id={minutesInputId} aria-describedby={minTimeDescriptionId} className={t18} placeholder="MM" />;
      $[52] = disabled;
      $[53] = minTimeDescriptionId;
      $[54] = minutes;
      $[55] = minutesInputId;
      $[56] = onMinutesChange;
      $[57] = t18;
      $[58] = t19;
    } else {
      t19 = $[58];
    }
    let t20;
    if ($[59] !== t17 || $[60] !== t19) {
      t20 = <div className="tw-relative tw-w-20">{t17}{t19}</div>;
      $[59] = t17;
      $[60] = t19;
      $[61] = t20;
    } else {
      t20 = $[61];
    }
    const t21 = `tw-ml-1 tw-rounded-lg tw-border-0 tw-px-3 tw-py-2 tw-transition-all tw-duration-200 ${disabled ? "tw-cursor-not-allowed tw-bg-[#2A2A33] tw-text-iron-500" : "tw-bg-[#2A2A33] tw-text-white tw-shadow-md hover:tw-translate-y-[-1px] hover:tw-bg-[#32323C] hover:tw-shadow-lg"}`;
    const t22 = isPm ? "PM" : "AM";
    let t23;
    if ($[62] !== disabled || $[63] !== t21 || $[64] !== t22 || $[65] !== toggleAmPm) {
      t23 = <button onClick={toggleAmPm} disabled={disabled} aria-label="Toggle AM/PM" className={t21}>{t22}</button>;
      $[62] = disabled;
      $[63] = t21;
      $[64] = t22;
      $[65] = toggleAmPm;
      $[66] = t23;
    } else {
      t23 = $[66];
    }
    if ($[67] !== t15 || $[68] !== t20 || $[69] !== t23) {
      t7 = <div className="tw-mb-5 tw-flex tw-items-center"><div className="tw-flex tw-flex-1 tw-items-center tw-space-x-2">{t15}{t16}{t20}{t23}</div></div>;
      $[67] = t15;
      $[68] = t20;
      $[69] = t23;
      $[70] = t7;
    } else {
      t7 = $[70];
    }
    if ($[71] !== minTimeDescription || $[72] !== minTimeDescriptionId) {
      t8 = minTimeDescription ? <p id={minTimeDescriptionId} className="tw-sr-only">{minTimeDescription}</p> : null;
      $[71] = minTimeDescription;
      $[72] = minTimeDescriptionId;
      $[73] = t8;
    } else {
      t8 = $[73];
    }
    t4 = "tw-grid tw-grid-cols-3 tw-gap-1.5";
    t5 = timeOptions.map(option => {
      const optionDisabled = disabled || isTimeDisabled(option.hours, option.minutes);
      const isSelected = !disabled && hours === option.hours && minutes === option.minutes;
      let optionStateClassName = "tw-bg-[#2A2A33] tw-text-iron-50 tw-shadow-sm hover:tw-translate-y-[-1px] hover:tw-bg-[#32323C] hover:tw-shadow-md";
      if (isSelected) {
        optionStateClassName = "tw-bg-primary-500 tw-text-white tw-ring-2 tw-ring-primary-400/30 hover:tw-bg-primary-600";
      } else {
        if (optionDisabled) {
          optionStateClassName = "tw-cursor-not-allowed tw-bg-[#2A2A33] tw-text-iron-600 tw-opacity-50";
        }
      }
      return <button key={option.label} onClick={() => !optionDisabled && onTimeChange(option.hours, option.minutes)} disabled={optionDisabled} className={`tw-rounded-lg tw-border-0 tw-p-1.5 tw-text-xs tw-transition-all tw-duration-200 sm:tw-text-sm ${optionStateClassName}`}>{option.label}</button>;
    });
    $[2] = disabled;
    $[3] = hours;
    $[4] = hoursInputId;
    $[5] = minTime;
    $[6] = minTimeDescription;
    $[7] = minTimeDescriptionId;
    $[8] = minutes;
    $[9] = minutesInputId;
    $[10] = onTimeChange;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[74] !== t4 || $[75] !== t5) {
    t10 = <div className={t4}>{t5}</div>;
    $[74] = t4;
    $[75] = t5;
    $[76] = t10;
  } else {
    t10 = $[76];
  }
  let t11;
  if ($[77] !== t10 || $[78] !== t6 || $[79] !== t7 || $[80] !== t8) {
    t11 = <div className={t6}>{t7}{t8}{t10}</div>;
    $[77] = t10;
    $[78] = t6;
    $[79] = t7;
    $[80] = t8;
    $[81] = t11;
  } else {
    t11 = $[81];
  }
  let t12;
  if ($[82] !== t11 || $[83] !== t9) {
    t12 = <div className={t9}>{t11}</div>;
    $[82] = t11;
    $[83] = t9;
    $[84] = t12;
  } else {
    t12 = $[84];
  }
  return t12;
}